import { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Accordion, BoxFull, Button, Dropdown, InputText, InputTextarea, TransactionalSubheader } from "iuvity-ui-components";
import { CustomInput, Header } from "../../components";
import {
  CustomDialog,
  DialogProps,
} from "../../components/dialog/CustomDialog";
import { TenantDataTableActions } from "../../components/tenants/TenantDatatableActions";
import { useTenants } from "../../hooks/useTenants";
import { AppContext } from "../../context";
import { Controller, useForm, useWatch } from "react-hook-form";
import { TenantCreatorSchema } from "../../validations/schemas";
import { ErrorMessage } from "@hookform/error-message";
import { ColorPicker } from "primereact/colorpicker";
import { FileUpload } from "primereact/fileupload";
import { useTenantBranding } from "../../hooks/useTenantBranding";
import { REVALIDATE_FORM_PROPS, TENANT_STATES } from "../../constants";
import { Tenant } from "../../interfaces";
import { Tooltip } from 'primereact/tooltip';

export const tenantCreatorDefaultValues = {
  id: "",
  name: "",
  shortname: "",
  status: "",
  primarycolor: "",
  secondarycolor: "",
  logo: "",
  isotypelogo: "",
  logoname: "",
  isotypelogoname: "",
  tenantcode: "",
};

export const TenantConfigurationPage = () => {
  const navigate = useNavigate();
  const {
    t,
    appState: { user, location, selectedTenant, tenants },
    setAppState,
  } = useContext(AppContext);

  const selectedTenantFormatted = {
    ...selectedTenant,
    logo: selectedTenant?.images?.filter((item:any) => item.entity === "TENANT_BRANDING_LOGO")[0],
    isotypelogo: selectedTenant?.images?.filter((item:any) => item.entity === "TENANT_BRANDING_ISOTYPE_LOGO")[0],
    logoname: selectedTenant?.images?.filter((item:any) => item.entity === "TENANT_BRANDING_LOGO")[0]?.name,
    isotypelogoname: selectedTenant?.images?.filter((item:any) => item.entity === "TENANT_BRANDING_ISOTYPE_LOGO")[0]?.name,
  }

  const {
    control,
    getValues,
    setValue,
    reset,
    formState: { errors, isDirty, isValid },
  } = useForm({
    mode: "onChange",
    delayError: 1200,
    reValidateMode: "onChange",
    defaultValues: {
      id: selectedTenant?.id,
      name: selectedTenant?.name,
      shortname: selectedTenant?.shortname,
      status: selectedTenant?.status,
      primarycolor: selectedTenant?.tenantbranding?.primarycolor?.slice(1,7),
      secondarycolor: selectedTenant?.tenantbranding?.secondarycolor?.slice(1,7),
      logo: selectedTenantFormatted?.logo || '',
      isotypelogo: selectedTenantFormatted?.isotypelogo || '',
      logoname: selectedTenantFormatted?.logoname || '',
      isotypelogoname: selectedTenantFormatted?.isotypelogoname || '',
      tenantcode: selectedTenant?.tenantcode,
    },
  });

  const { tenantAction } = useTenants("ACTIONS");
  const { getBrandingInfo } = useTenantBranding("DATA");
  const [dataBranding, setDataBranding] = useState<any>();


  useEffect(()=>{
    (async () => {
      const data = await Promise.resolve(getBrandingInfo(selectedTenant?.id!));
      setDataBranding(data);
    })();
  },[]);

  const [modalProps, setModalProps] = useState<DialogProps>({
    visible: false,
  } as DialogProps);

  const [layerName, setLayerName] = useState<{
    logo: string,
    isotypelogo: string,
  }>({
    logo: selectedTenantFormatted?.logoname || '',
    isotypelogo: selectedTenantFormatted?.isotypelogoname || ''
  })

  const [layerNameValidator,setLayerNameValidator] = useState<{
    logo: string,
    isotypelogo: string,
  }>({
    logo: selectedTenantFormatted?.logoname || '',
    isotypelogo: selectedTenantFormatted?.isotypelogoname || ''
  })

  const [hasImageError,setHasImageError] = useState<{
    logo: boolean,
    isotypelogo: boolean,
  }>({
    logo: true,
    isotypelogo: true
  })
  
  const [disableUploadFile, setDisableUploadFile] = useState<{
    logo: boolean;
    isotypelogo: boolean;
  }>({
    logo: false,
    isotypelogo: false,
  })

  const invoiceUploadHandler = ({ files }: any, key: 'logo' | 'isotypelogo') => {
    setDisableUploadFile((prev:any)=>({
      ...prev,
      [key]: true,
    }));
    setValue(key, files[0], REVALIDATE_FORM_PROPS);
    const splitLayerName = files[0].name.split('.')
    const formatLayerName = `${splitLayerName[0].slice(0,20)}.${splitLayerName[splitLayerName.length - 1]}`;
    setLayerNameValidator((prev)=>({
      ...prev,
      [key]: splitLayerName[0].slice(0,20)
    }));
    key === 'logo'
    ? setValue('logoname', splitLayerName[0].slice(0,20))
    : setValue('isotypelogoname', splitLayerName[0].slice(0,20));
    setLayerName((prev)=>({
      ...prev,
      [key]: formatLayerName
    }));
  }
  
  const invoiceRemoveHandler = (key: 'logo' | 'isotypelogo') => {
    setDisableUploadFile((prev:any)=>({
      ...prev,
      [key]: false,
    }));
    // @ts-ignore
    setValue(key, selectedTenantFormatted?.[key], REVALIDATE_FORM_PROPS);
    key === 'logo'
    ? setValue('logoname', selectedTenantFormatted?.logoname!, REVALIDATE_FORM_PROPS)
    : setValue('isotypelogoname', selectedTenantFormatted?.isotypelogoname!, REVALIDATE_FORM_PROPS);
    setLayerName((prev)=>({
      ...prev,
      [key]: key === 'logo' ? selectedTenantFormatted?.logoname : selectedTenantFormatted?.isotypelogoname
    }));
    setLayerNameValidator((prev)=>({
      ...prev,
      [key]: key === 'logo' ? selectedTenantFormatted?.logoname : selectedTenantFormatted?.isotypelogoname
    }));
  }

  const logoValues = useWatch({
    control: control,
    name: "logo",
  });

  const isotypelogoValues = useWatch({
    control: control,
    name: "isotypelogo",
  });

  const tenantcodeValues = useWatch({
    control: control,
    name: "tenantcode",
  });

  const onSave = async () => {
    const formValues = getValues();

    const actionStatus = await tenantAction("UPDATE",
      {
        ...formValues,
        logo: getValues('logo')['id'] ? '' : getValues('logo'),
        isotypelogo: getValues('isotypelogo')['id'] ? '' : getValues('isotypelogo'),
        primarycolor: `#${formValues.primarycolor}`,
        secondarycolor: `#${formValues.secondarycolor}`,
        logoName: formValues.logoname,
        isotypelogoName: formValues.isotypelogoname,
        updatedby: user?.name,
      },
    );

    if (actionStatus === "SUCCESS") {
      reset(formValues);
      setAppState((prev) => ({
        ...prev,
        selectedTenant: formValues as Tenant,
      }));
    }
  };

  const onCancel = () => {
    navigate("/tenants");
  };

  const handleModalProps = (isCancelButton?: boolean) => {
    setModalProps((prev) => ({
      ...prev,
      visible: true,
      title: isCancelButton
        ? t("Tenants.cancelModalTitle")
        : t("Tenants.saveModalTitle"),
      content: isCancelButton
        ? t("Tenants.cancelModalContent")
        : t("Tenants.saveModalContent"),
      onConfirm: isCancelButton ? onCancel : onSave,
    }));
  };

  useEffect(()=>{
    setHasImageError({
      logo: false,
      isotypelogo: false
    })
    if (layerNameValidator.logo !== '' && !(/^([a-zA-Z0-9\\sÁÉÍÓÚáéíóúÑñ_]+)$/.test(layerNameValidator.logo))) {
      setHasImageError((prev) => ({
        ...prev,
        logo: true,
      }))
    }
    if (layerNameValidator.isotypelogo !== '' && !(/^([a-zA-Z0-9\\sÁÉÍÓÚáéíóúÑñ_]+)$/.test(layerNameValidator.isotypelogo))) {
      setHasImageError((prev) => ({
        ...prev,
        isotypelogo: true,
      }))
    }
    layerNameValidator.logo === '' &&
      setHasImageError((prev) => ({
        ...prev,
        logo: true,
      }));
    layerNameValidator.isotypelogo === '' &&
      setHasImageError((prev) => ({
        ...prev,
        isotypelogo: true,
      }));
  },[layerNameValidator])


  return (
    <>
      {/* <Header
        welcomeMessage={t("Dashboard.welcomeTitle")}
        userName={user?.name}
      /> */}
      <TransactionalSubheader
        subheaderTitle={
          <div style={{ position: "relative", left: "-40px" }}>
            <i
              className="iu iu-icon-left pr-3"
              onClick={() => navigate("/tenants")}
              aria-hidden="true"
              style={{
                fontSize: "var(--font-size-tablet-title)",
                cursor: "pointer",
              }}
            />
            {t("Tenants.editTenantTitle")}
          </div>
        }
      >
        <TransactionalSubheader.BreadCrumb
          id={t("Tenants.editTenantTitle")}
          disabledDefaultHome={true}
          breadcrumbsHomeItem={{
            icon: "iu iu-icon-home",
            command: () => navigate("/"),
          }}
          breadcrumbsItems={[
            {
              label: t("Tenants.advancedOptionsBreadcrumbLabel"),
              style: { fontWeight: "bold" },
              disabled: true
            },
            {
              label: t("Tenants.tenantsBreadcrumbLabel"),
              command: () => navigate("/Tenants"),
              style: { fontFamily: "var(--fontBarlowSemiBold)" },
            },
            {
              label: t("Tenants.editTenantTitle"),
              disabled: true
            },
          ]}
        />
        <TransactionalSubheader.Title />
      </TransactionalSubheader>

      <BoxFull className="flex align-items-center justify-content-center pt-5 pb-0" variant="tertiary">
        <div
          style={{
            display: "flex",
            flexDirection: "column",
          }}
        >
          <p style={{ marginBottom: 'var(--space-base)' }}>{
            t("Tenants.editTenantSubtitle")}
          </p>
          <div className="form-container md py-5 w-full">
            <div className="form md w-full">
              <div className="flex align-items-flex-start justify-content-flex-start flex-wrap">
                {Object.keys(tenantCreatorDefaultValues).map((key, idx, arr) => (
                  <div
                    key={`c-${key}`}
                    className={`flex flex-column mb-1 px-6`}
                    style={{ width: "50%" }}
                  >
                    <Controller
                      key={key}
                      name={key as any}
                      control={control}
                      rules={(TenantCreatorSchema(t) as any)[key]}
                      render={({ field }) => {
                        return (key === "id" || key === "name" || key === "shortname") ? (
                          <div className="input-tenant" style={{ paddingLeft: "3rem" }}>
                            <InputText
                              id={`input-tenant-${key}`}
                              className={
                                // @ts-ignore
                                errors[key]?.message
                                  ? "p-invalid"
                                  : idx !== arr.length - 1
                                  ? `mb-3 ${key === 'id' ? 'rd-only' : ''}`
                                  : ""
                              }
                              label={t(`Tenants.${key}Label`)}
                              placeholder={t(`Tenants.${key === "shortname" ? "alias" : key}Placeholder`)}
                              type="text"
                              autoComplete="off"
                              style={{
                                width: '10rem !important',
                              }}
                              keyfilter={
                                (TenantCreatorSchema(t) as any)[key]?.keyfilter
                              }
                              maxLength={
                                (TenantCreatorSchema(t) as any)[key]?.maxLength?.value
                              }
                              {...field}
                              isReadOnly={key === 'id' ? true : false}
                            />
                          </div>
                        ) : (key === "status") ? (
                          <div style={{ paddingLeft: "3rem" }}>
                            <Dropdown
                              key={key}
                              id={`dropdown-tenant-${key}`}
                              className="dropdown dropdown-tenant"
                              label={t(`Tenants.statusLabel`)}
                              placeholder={t(`Tenants.statusPlaceholder`)}
                              options={TENANT_STATES(t)}
                              filter={false}
                              showClear={false}
                              width="20rem"
                              {...field}
                            />
                          </div>
                        ) : (key === "tenantcode") ? (
                          <div style={{ paddingLeft: "3rem" }}>
                            <InputTextarea
                              id={`input-tenant-${key}`}
                              key={key}
                              className={`input-md ${errors[key]?.message ? "p-invalid" : ""}`}
                              label={t(`Tenants.${key}Label`)}
                              placeholder={t(`Tenants.${key}Placeholder`)}
                              autoComplete="off"
                              style={{
                                width: "20rem",
                                overflow: "auto"
                              }}
                              keyfilter={
                                (TenantCreatorSchema(t) as any)[key]?.keyfilter
                              }
                              maxLength={
                                (TenantCreatorSchema(t) as any)[key]?.maxLength?.value
                              }
                              {...field}
                            />
                          </div>
                        ) : <></>
                      }}
                    />
                    { 
                      ['name', 'shortname', 'tenantcode'].includes(key) &&
                        <ErrorMessage
                          errors={errors}
                            name={key as any}
                            //@ts-ignore
                            render={({ message }) => (
                              <small
                                key={`small-${key}`}
                                className="p-error block"
                                style={{
                                  marginTop: "0.5rem",
                                  marginBottom: "1.5rem",
                                  marginLeft: "3rem",
                                }}
                              >
                                {message}
                              </small>
                          )}
                        />
                    }
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="colors_title">
            <span>{t("Tenants.customerColorsTitle")}</span>
          </div>
          <div className="form-container md py-5 w-full">
            <div className="form md w-full">
              <div className="flex align-items-flex-start justify-content-flex-start flex-wrap">
                {Object.keys(tenantCreatorDefaultValues).filter((key) => key !== 'id' && key !== 'status').map((key, idx, arr) => (
                  <div
                    key={`c-${key}`}
                    className={`flex flex-column mb-0 px-6`}
                    style={{ width: "50%" }}
                  >
                    <Controller
                      key={key}
                      name={key as any}
                      control={control}
                      rules={(TenantCreatorSchema(t) as any)[key]}
                      render={({ field }) => {
                        return (key === "primarycolor" || key === "secondarycolor") ? (
                          <div style={{ paddingLeft: "3rem" }}>
                            <>
                              {['primarycolor','secondarycolor'].includes(key) &&
                                <div className="mb-2" style={{display: 'flex', alignItems: 'center', justifyContent: 'flex-start'}}>
                                  <label htmlFor={`input-${key}`} className="block lb ">
                                    {t(`Tenants.${key}Label`)}
                                  </label>
                                  <div className="tenant-tooltip">
                                    <Tooltip target=".custom-target-icon" />
                                    <i 
                                      className="custom-target-icon iu iu-icon-info" 
                                      data-pr-tooltip={t(`Tenants.${key}Tooltip`)}
                                      data-pr-position="right"
                                      style={{ color: "var(--successColor)" }}
                                    />
                                  </div>
                                </div>
                              }
                              <div className={`${['primarycolor','secondarycolor'].includes(key) ? 'flex' : ''}`}>
                                {['primarycolor','secondarycolor'].includes(key) &&
                                  (
                                    <>
                                      <ColorPicker
                                        id="tenant-colorPicker"
                                        format="hex"
                                        value={getValues(key)}
                                        defaultColor="#cccccc"
                                        readOnly={true}
                                        style={{
                                          marginRight: "var(--marginLabelInput)",
                                        }}
                                        onClick={()=>{}}
                                      />
                                      <p>#</p>
                                    </>
                                  )
                                }
                                <CustomInput
                                  id={`input-${key}`}
                                  className={
                                    // @ts-ignore
                                    errors[key]?.message
                                      ? "p-invalid"
                                      : idx !== arr.length - 1
                                      ? "mb-3"
                                      : ""
                                  }
                                  label={['primarycolor','secondarycolor'].includes(key) ? undefined : t(`Tenants.${key}Label`)}
                                  placeholder={t(`Tenants.${key}Placeholder`)}
                                  type="text"
                                  autoComplete="off"
                                  style={{
                                    width: '10rem !important',
                                  }}
                                  keyfilter={
                                    (TenantCreatorSchema(t) as any)[key]?.keyfilter
                                  }
                                  maxLength={
                                    (TenantCreatorSchema(t) as any)[key]?.maxLength?.value
                                  }
                                  {...field}
                                />
                              </div>
                            </>
                          </div>
                        ) : <></>
                      }}
                    />
                    { 
                      ['primarycolor', 'secondarycolor'].includes(key) &&
                        <ErrorMessage
                          errors={errors}
                            name={key as any}
                            //@ts-ignore
                            render={({ message }) => (
                              <small
                                key={`small-${key}`}
                                className="p-error block"
                                style={{
                                  marginTop: "0.5rem",
                                  marginBottom: "1.5rem",
                                  marginLeft: "3rem",
                                }}
                              >
                                {message}
                              </small>
                          )}
                        />
                    }
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="logotype_title">
            <span>{t("Tenants.customerLogoTitle")}</span>
            <Tooltip target=".custom-target-icon" />
            <i 
              className="custom-target-icon iu iu-icon-info" 
              data-pr-tooltip={t(`Tenants.uploadFileLogoTooltip`)}
              data-pr-position="right"
              style={{ color: "var(--successColor)", marginLeft: "0.5rem" }}
            />
          </div>
          <p style={{ margin: '0 2rem var(--space-base) 2rem' }}>{
            t("Tenants.customerLogoSubtitle")}
          </p>
          <div>
            {Object.keys(tenantCreatorDefaultValues).map((key, idx, arr) => (
              <div
                key={`c-${key}`}
              >
                <Controller
                  key={key}
                  name={key as any}
                  control={control}
                  rules={(TenantCreatorSchema(t) as any)[key]}
                  render={({ field }) => {
                    return (key === "logo") ? (
                        <div className="card">
                          <FileUpload
                          accept="image/*"
                          // maxFileSize={1000000}
                          emptyTemplate={
                            <div
                              className="flex align-items-center pl-3"
                              style={{
                                height: '100%',
                                justifyContent: 'space-around',
                              }}
                            >
                              <div
                                style={{
                                  padding: '1rem',
                                }}
                              >
                                <img
                                  // src="https://dev.w3.org/SVG/tools/svgweb/samples/svg-files/410.svg"
                                  src={dataBranding?.logo?.url}
                                  alt="logo"
                                  style={{
                                    height: '50px',
                                    width: 'var(--padding-level-eighty)',
                                  }}
                                />
                              </div>
                              <div>
                                {`${selectedTenantFormatted?.logoname}.${selectedTenantFormatted?.logo?.metadata?.mimetype?.split('/')[1].split('+')[0]}`}
                              </div>
                              <div>
                                {`${(selectedTenantFormatted?.logo?.metadata?.size / 1028).toFixed(2)} KB`}
                              </div>
                            </div>
                          }
                          cancelOptions={{
                            style: {
                              display: 'none',
                            }
                          }}
                          uploadOptions={{
                            style: {
                              display: 'none',
                            }
                          }}
                          chooseLabel={
                            disableUploadFile.logo ? t("PointsOfAttention.fileLabel") : t("Tenants.replaceFileBtnLabel")
                          }
                          chooseOptions={{
                            icon: 'iu iu-icon-upload',
                            className: disableUploadFile.logo ? ' p-disabled p-button-link' : 'p-button-link',
                          }}
                          onSelect={(e) => invoiceUploadHandler(e, 'logo')}
                          onRemove={()=> invoiceRemoveHandler('logo')}
                        />
                        </div>
                    ) : <></>
                  }}
                />
                { ['logo'].includes(key) &&
                  hasImageError.logo
                  ?
                    <small
                      key={`small-logo`}
                      className="p-error block"
                      style={{
                        marginTop: "0.5rem",
                        marginBottom: "1.5rem",
                      }}
                    >
                      {
                        layerNameValidator.logo === ''
                        ? 
                          !selectedTenantFormatted?.logo
                            ? (<ErrorMessage
                                errors={errors}
                                name="logo"
                                //@ts-ignore
                                render={({ message }) => (
                                  <small
                                    className="p-error block"
                                    style={{
                                      marginTop: "0.5rem",
                                    }}
                                  >
                                    {message}
                                  </small>
                                )}
                              />)
                            : null
                        : t("GlobalConfigurations.generalErrorMessage")
                      }
                    </small>
                  : null
                }
              </div>
            ))}
          </div>

          <div className="logotype_title">
            <span>{t("Tenants.customerIsotypeTitle")}</span>
            <Tooltip target=".custom-target-icon" />
            <i 
              className="custom-target-icon iu iu-icon-info" 
              data-pr-tooltip={t(`Tenants.uploadFileIsotypeTooltip`)}
              data-pr-position="right"
              style={{ color: "var(--successColor)", marginLeft: "0.5rem" }}
            />
          </div>
          <div>
            {Object.keys(tenantCreatorDefaultValues).map((key, idx, arr) => (
              <div
                key={`c-${key}`}
              >
                <Controller
                  key={key}
                  name={key as any}
                  control={control}
                  rules={(TenantCreatorSchema(t) as any)[key]}
                  render={({ field }) => {
                    return (key === "isotypelogo") ? (
                        <div className="card">
                          <FileUpload
                            accept="image/*"
                            // maxFileSize={1000000}
                            emptyTemplate={
                              <div
                                className="flex align-items-center pl-3"
                                style={{
                                  height: '100%',
                                  justifyContent: 'space-around',
                                }}
                              >
                                <div
                                  style={{
                                    padding: '1rem',
                                  }}
                                >
                                  <img
                                    src={dataBranding?.isotypelogo?.url}
                                    alt="isotype"
                                    style={{
                                      height: '50px',
                                      width: 'var(--padding-level-eighty)',
                                    }}
                                  />
                                </div>
                                <div>
                                  {`${selectedTenantFormatted?.isotypelogoname}.${selectedTenantFormatted?.isotypelogo?.metadata?.mimetype?.split('/')[1].split('+')[0]}`}
                                </div>
                                <div>
                                  {`${(selectedTenantFormatted?.isotypelogo?.metadata?.size / 1028).toFixed(2)} KB`}
                                </div>
                              </div>
                            }
                            cancelOptions={{
                              style: {
                                display: 'none',
                              }
                            }}
                            uploadOptions={{
                              style: {
                                display: 'none',
                              }
                            }}
                            chooseLabel={
                              disableUploadFile.isotypelogo ? t("PointsOfAttention.fileLabel") : t("Tenants.replaceFileBtnLabel")
                            }
                            chooseOptions={{
                              icon: 'iu iu-icon-upload',
                              className: disableUploadFile.isotypelogo ? ' p-disabled p-button-link' : 'p-button-link',
                            }}
                            onSelect={(e) => invoiceUploadHandler(e, 'isotypelogo')}
                            onRemove={()=> invoiceRemoveHandler('isotypelogo')}
                          />
                        </div>
                    ) : <></>
                  }}
                />
                { ['isotypelogo'].includes(key) &&
                  hasImageError.isotypelogo
                  ?
                    <small
                      key={`small-isotypelogo`}
                      className="p-error block"
                      style={{
                        marginTop: "0.5rem",
                        marginBottom: "1.5rem",
                      }}
                    >
                      {
                        layerNameValidator.isotypelogo === ''
                        ? 
                          !selectedTenantFormatted?.isotypelogo
                            ? (<ErrorMessage
                                errors={errors}
                                name="isotypelogo"
                                //@ts-ignore
                                render={({ message }) => (
                                  <small
                                    className="p-error block"
                                    style={{
                                      marginTop: "0.5rem",
                                    }}
                                  >
                                    {message}
                                  </small>
                                )}
                              />)
                            : null
                        : t("GlobalConfigurations.generalErrorMessage")
                      }
                    </small>
                  : null
                  }
              </div>
            ))}
          </div>

        </div>
      </BoxFull>

      <BoxFull className="pt-5 px-4 pb-5" style={{ backgroundColor: "transparent" }}>
        <div className="flex align-items-center justify-content-end gap-5">
          <Button
            variant="p-button-link"
            label={t("Tenants.cancelBtnLabel")}
            onClick={!isDirty ? onCancel : () => handleModalProps(true)}
          />
          <Button
            variant="p-button"
            label={t("Tenants.saveBtnLabel")}
            disabled={!(
              (isDirty &&
              isValid &&
              (logoValues && (layerName.logo !== selectedTenantFormatted?.logoname || isDirty)) &&
              (isotypelogoValues && (layerName.isotypelogo !== selectedTenantFormatted?.isotypelogoname || isDirty)) &&
              !hasImageError.logo &&
              !hasImageError.isotypelogo)
            )}
            onClick={() => handleModalProps()}
          />
        </div>
      </BoxFull>

      <BoxFull className="pt-0" variant="tertiary">
        <Accordion
          activeIndex={
            location.prevLocation === "/tenants/edit" &&
            location.currentLocation === "/applications/edit"
              ? 1
              : 0
          }
          id="accordion"
          items={[
            {
              children: (
                <TenantDataTableActions auxModalController={setModalProps} />
              ),
              disabled: false,
              header: t("Tenants.applicationsInfoLabel"),
              key: "1122334",
            },
          ]}
        />
      </BoxFull>

      <CustomDialog
        title={modalProps.title}
        visible={modalProps.visible}
        content={modalProps.content}
        handleModalState={setModalProps}
        onConfirm={modalProps.onConfirm}
      />
    </>
  );
};
